/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Icon, Statistic, StatisticLabel, StatisticValue } from 'semantic-ui-react'
import { CardContent } from '../../../Shared/Card'
import React from 'react'
import useUser from '../../../../Shared/UserProvider/useUser'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { CardTitle } from '../../../Shared/CardTitle'
import { intlFormatMoneyWithCompact, ONE_THOUSAND } from '../../../../../helpers/money'
import { Link } from 'react-router-dom-v5-compat'
import { useTranslation } from 'react-i18next'
import { MissedRevenueBreakdownBar } from './MissedRevenueBreakdownBar'
import { RateRadialChart } from '../../../../Shared/RateRadialChart'
import { THEME_COLORS } from '../../../../Shared/theme'


const getColor = (rate) => {
    if (rate === 0) return '#e5e7eb'
    if (rate >= 70) return THEME_COLORS.GREEN
    if (rate >= 50) return THEME_COLORS.ORANGE
    return THEME_COLORS.RED
}

const MissedRevenueCardContent = ({
                                      from,
                                      to,
                                      totalMissedRevenue,
                                      revenuesMissed,
                                      totalMissedRevenueUnavailable,
                                      totalMissedRevenueSubstituted,
                                      totalMissedQuantity,
                                      totalMissedSubstitutedQuantity
                                  }) => {
    const [t] = useTranslation()
    const {user} = useUser()
    const currency = user.getCompanyCurrency()
    const locale = user.getCompanyLocale()

    const fmt = (amount) => intlFormatMoneyWithCompact(amount, currency, locale, ONE_THOUSAND)

    return (
      <CardContent
        css={css`
            display: grid;
            place-items: center;
            gap: .5rem;
            grid-template-rows: 30px 1fr 1fr 100px;
            grid-template-areas:
                'title'
                'missedRevenues'
                'radialChart'
                'breakdown';
        `}
      >
          <CardTitle title={format(new Date(), 'MMMM', {locale: fr})}/>

          <Statistic
            color="red"
            size="small"
            css={css`grid-area: missedRevenues;

                &.ui.statistic, &.ui.statistic + .ui.statistic {
                    margin: 0;
                }`}
          >
              <StatisticValue>{fmt(totalMissedRevenue)}</StatisticValue>
              <StatisticLabel>{t('dashboard.missed_revenues.total_missed_revenues')}</StatisticLabel>
          </Statistic>

          <div
            css={css`
                grid-area: radialChart;
                display: flex;
                align-items: flex-end;
            `}
          >
              <RateRadialChart
                css={css`
                    margin-top: -1.5rem;
                    margin-bottom: -2rem;
                `}
                labelElement={
                    <Link
                      to={{ pathname: '/analytics/unavailable-items', search: new URLSearchParams({ from, to }).toString() }}
                      css={css`
                          display: flex;
                          align-items: center;
                          gap: .5rem;
                          font-family: Helvetica, Arial, sans-serif;
                          text-decoration: none;
                          color: #333;
                      `}
                    >
                        {t('unavailable_items.summary.substitution_rate')}
                        <Icon name="arrow right" fitted size="small"/>
                    </Link>
                }
                rate={totalMissedRevenueUnavailable > 0 ?
                  Math.round((totalMissedSubstitutedQuantity / totalMissedQuantity) * 100) : 0}
                animationsEnabled
                getColor={getColor}
              />
          </div>

          <div css={css`
              grid-area: breakdown;
              align-self: end;
              width: 100%;
          `}>
              <MissedRevenueBreakdownBar
                revenuesMissed={revenuesMissed}
                totalMissedRevenueUnavailable={totalMissedRevenueUnavailable}
                totalMissedRevenueSubstituted={totalMissedRevenueSubstituted}
                formatFn={fmt}
              />
          </div>
      </CardContent>
    )
}

export { MissedRevenueCardContent }
